"use client";

import React, { useEffect, useMemo, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import {
  ApiConfig,
  API_ORDER_ROUTER,
  API_SERVICE_TYPE_MAPS_ROUTER,
  API_SERVICE_ADDON_ROUTER,
} from "../../../services/api-config";

/**
 * Chọn lại dịch vụ khi ĐỔI CHI NHÁNH của đơn.
 *
 * Vì sao phải chọn lại: `order_details.item_id` là id của dòng ánh xạ dịch vụ × CHI NHÁNH
 * (`service_type_map`), không phải id dịch vụ. Chi nhánh khác có dòng map khác id kể cả khi danh mục đã
 * được copy y hệt, nên không thể mang id cũ sang.
 *
 * Để đỡ phải nhập tay lại từ đầu, component tự **ghép theo TÊN** ("Dịch vụ - Loại") giữa các mục của đơn
 * và danh mục chi nhánh mới. Mục nào không khớp tên thì để trống, người dùng tự chọn.
 */

export const ITEM_TYPE = { SERVICE: 1, ADDON: 2 };

export interface PickedItem {
  item_id: number;
  item_type: number;
  quantity: number;
}

interface Option {
  id: number;
  label: string;
  price: number;
  item_type: number;
}

interface Props {
  orderId: number;
  branchId: number;
  onChange: (items: PickedItem[], ready: boolean) => void;
}

/** Chuẩn hoá tên để so khớp: bỏ dấu cách thừa, thường hoá — tên copy giữa chi nhánh thường chỉ khác vậy. */
const normalize = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

const BranchItemsPicker: React.FC<Props> = ({ orderId, branchId, onChange }) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [rows, setRows] = useState<{ key: string; oldName: string; optionId: number; quantity: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { request: getOrder } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });
  const { request: getMaps } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });
  const { request: getAddons } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.PRODUCT_SERVICE });

  useEffect(() => {
    if (!orderId || !branchId) return;
    let alive = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [orderRes, mapRes, addonRes] = await Promise.all([
          getOrder({ endPoint: API_ORDER_ROUTER.API_DETAIL(orderId) }),
          getMaps({
            endPoint: API_SERVICE_TYPE_MAPS_ROUTER.API_LIST,
            data: { status: 1, branch_id: branchId, store_id: branchId, limit: 1000 },
          }),
          getAddons({
            endPoint: API_SERVICE_ADDON_ROUTER.API_LIST,
            data: { status: 1, branch_id: branchId, store_id: branchId, limit: 1000 },
          }),
        ]);
        if (!alive) return;

        const opts: Option[] = [];
        if (mapRes && isSuccess(mapRes)) {
          for (const m of (mapRes as any)?.data?.list ?? []) {
            const svc = m.service?.name || m.service_name || "";
            const type = m.serviceType?.name || m.service_type?.name || m.service_type_name || "";
            opts.push({
              id: Number(m.id),
              label: [svc, type].filter(Boolean).join(" - ") || `#${m.id}`,
              price: Number(m.price) || 0,
              item_type: ITEM_TYPE.SERVICE,
            });
          }
        }
        if (addonRes && isSuccess(addonRes)) {
          for (const a of (addonRes as any)?.data?.list ?? []) {
            opts.push({
              id: Number(a.id),
              label: a.name || `#${a.id}`,
              price: Number(a.price) || 0,
              item_type: ITEM_TYPE.ADDON,
            });
          }
        }
        setOptions(opts);

        // Ghép theo tên với các mục hiện có của đơn (BE trả `item_name` = "Dịch vụ - Loại").
        const byName = new Map<string, Option>();
        for (const o of opts) if (!byName.has(normalize(o.label))) byName.set(normalize(o.label), o);
        const oldItems: any[] = (orderRes as any)?.data?.items ?? [];
        setRows(
          oldItems.map((it, idx) => {
            const matched = byName.get(normalize(String(it.item_name || "")));
            return {
              key: `${idx}`,
              oldName: String(it.item_name || "—"),
              optionId: matched?.id ?? 0,
              quantity: Number(it.quantity) || 1,
            };
          }),
        );
        if (opts.length === 0) setError("Chi nhánh này chưa có dịch vụ nào trong danh mục");
      } catch {
        if (alive) setError("Không tải được danh mục dịch vụ của chi nhánh");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [orderId, branchId]);

  const optionById = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  // Đẩy kết quả ra ngoài mỗi khi đổi. `ready` = đã chọn đủ, chưa đủ thì nút Lưu bị khoá.
  useEffect(() => {
    const picked: PickedItem[] = rows
      .filter((r) => r.optionId > 0)
      .map((r) => ({
        item_id: r.optionId,
        item_type: optionById.get(r.optionId)?.item_type ?? ITEM_TYPE.SERVICE,
        quantity: r.quantity,
      }));
    const hasService = picked.some((p) => p.item_type === ITEM_TYPE.SERVICE);
    const allFilled = rows.length > 0 && rows.every((r) => r.optionId > 0);
    onChange(picked, allFilled && hasService);
    // eslint-disable-next-line
  }, [rows, optionById]);

  const total = rows.reduce((s, r) => s + (optionById.get(r.optionId)?.price || 0) * r.quantity, 0);
  const unmatched = rows.filter((r) => !r.optionId).length;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <p className="text-sm font-semibold text-amber-800 mb-1">Chọn lại dịch vụ theo chi nhánh mới</p>
      <p className="text-xs text-amber-700 mb-2">
        Mã dịch vụ gắn theo từng chi nhánh nên không mang sang được. Hệ thống đã ghép sẵn theo tên, bạn kiểm
        tra lại các dòng còn trống.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500">Đang tải danh mục…</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-500">Đơn không có dịch vụ nào.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={r.key} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-slate-500 truncate" title={r.oldName}>{r.oldName}</div>
                <select
                  value={r.optionId || ""}
                  onChange={(e) => {
                    const v = Number(e.target.value) || 0;
                    setRows((prev) => prev.map((x, k) => (k === i ? { ...x, optionId: v } : x)));
                  }}
                  className={`w-full border rounded-lg px-2 py-1.5 text-sm bg-white ${r.optionId ? "" : "border-red-400"}`}
                >
                  <option value="">— Chọn dịch vụ —</option>
                  {options.map((o) => (
                    <option key={`${o.item_type}-${o.id}`} value={o.id}>
                      {o.label} ({o.price.toLocaleString("vi-VN")}đ)
                    </option>
                  ))}
                </select>
              </div>
              <input
                type="number"
                min={1}
                value={r.quantity}
                onChange={(e) => {
                  const q = Math.max(1, Number(e.target.value) || 1);
                  setRows((prev) => prev.map((x, k) => (k === i ? { ...x, quantity: q } : x)));
                }}
                className="w-16 border rounded-lg px-2 py-1.5 text-sm text-center bg-white"
              />
              <button
                type="button"
                onClick={() => setRows((prev) => prev.filter((_, k) => k !== i))}
                className="text-slate-400 hover:text-red-600 px-1"
                title="Bỏ mục này"
              >
                ✕
              </button>
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={() =>
                setRows((prev) => [...prev, { key: `new-${Date.now()}`, oldName: "Thêm mới", optionId: 0, quantity: 1 }])
              }
              className="text-xs font-semibold text-amber-700 hover:underline"
            >
              + Thêm dịch vụ
            </button>
            <span className="text-sm text-slate-700">
              Tạm tính: <b className="text-amber-700">{total.toLocaleString("vi-VN")}đ</b>
            </span>
          </div>

          {unmatched > 0 && (
            <p className="text-xs text-red-600">Còn {unmatched} dòng chưa chọn dịch vụ.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default BranchItemsPicker;
